import * as React from "react";
import classnames from 'classnames';
import IComparisonItem from "../interfaces/frontend/IComparisonItem";
import { ReactNode } from "react";
import { CheckedSpinner } from "./section/checked-spinner";

interface Props {
  title?: string,
  mutant: string,
  items: IComparisonItem[],
  icons: {
    noChange: React.ReactNode,
    decreaseLarge: React.ReactNode,
    decreaseModerate: React.ReactNode,
    increaseLarge: React.ReactNode,
    increaseModerate: React.ReactNode,
  },
  iconFirst: boolean,
  differences: boolean,
  details: boolean,
}

interface ICmp {
  noChange: boolean,
  decrease: boolean,
  delta: number,
  deltaStr: string,
  cls: string,
  icon: React.ReactNode,
}

export function cmpVals({wtVal, mutVal, large}: IComparisonItem, icons: Props['icons']): ICmp {
  const noChange = wtVal == mutVal;
  const decrease = mutVal < wtVal;
  const delta = mutVal - wtVal;
  const deltaStr = '∆' + (delta == 0 ? '0.0' : ((decrease ? '' : '+') + delta.toFixed(1)));
  const cls = noChange ? 'no-change' : decrease ? 'decrease' : 'increase';
  let icon: ReactNode | null;
  switch (true) {
    case noChange:
      icon = icons.noChange;
      break;
    case decrease && large:
      icon = icons.decreaseLarge;
      break;
    case decrease && !large:
      icon = icons.decreaseModerate;
      break;
    case !decrease && large:
      icon = icons.increaseLarge;
      break;
    case !decrease && !large:
      icon = icons.increaseModerate;
      break;
    default:
      icon = null;
  }
  return { noChange, decrease, delta, deltaStr, cls, icon };
}

export default function CatalyticResiduesBoxContent(props: Props) {

  function renderDetailItem(item: IComparisonItem, max: number, differences: boolean = false) {
    const { deltaStr, cls, icon } = cmpVals(item, props.icons);
    return (
      <div className="ll-result-detail-item" key={item.name}>
        <div className={classnames('ll-grid', {'ll-differences': differences})}>
          <div className="ll-right">WT</div>
          <div />
          {differences ? <div /> : null}
          <div>{renderSingleBar(item.wtVal, max, 'll-no-change')}</div>
          <div className="ll-right">{props.mutant}</div>
          <div>{icon}</div>
          {differences ? <div className="ll-delta">{deltaStr}</div> : null}
          <div>{renderSingleBar(item.mutVal, max, 'll-'+cls)}</div>
        </div>
        <div className="ll-center">
          <span>{item.name}</span>
        </div>
      </div>
    );
  }

  function renderSingleBar(value: number, max: number, innerCls: string) {
    // normalized - bar width in percent (between 0 and 100)
    const normalized = Math.min(Math.max(0, Math.round(value * 100 / max)), 100)
    return <div style={{
      width: '100px',
      height: '20px',
      display: 'flex',
    }}>
      <div className={`ll-single-bar-text ll-left ${innerCls}`} style={{
        flex: `0 0 ${normalized}%`,
      }}>
        <span>{value}</span>
      </div>
    </div>
  }

  if (props.items === null) {
    return (
      <div className="ll-section-body ll-section-spinner">
        <CheckedSpinner withText={true}/>
      </div>
    )
  } else if (!props.details) {
    return (
      <div className={classnames('ll-section-body', {
        "ll-icon-row-label-first": !props.iconFirst,
        "ll-icon-row-icon-first": props.iconFirst,
      })} style={{
        paddingLeft: '4rem',
      }}>
        <span style={{marginRight: '0.8rem'}}>{props.title ? props.title : props.mutant}</span>
        {props.items.map(item => {
          const { deltaStr, icon } = cmpVals(item, props.icons);
          const shortName = item.shortName ? item.shortName : item.name;
          return <React.Fragment key={shortName}>
            {props.iconFirst && icon}
            {shortName}
            {!props.iconFirst && icon}
            {props.differences && <span className="ll-delta">{deltaStr}</span>}
          </React.Fragment>;
        })}
      </div>
    )
  } else {
    const detailBarMax = Math.max(...props.items.flatMap(item => [item.wtVal, item.mutVal]))
    return (
      <div className="ll-section-body ll-section-flex">
        {props.items.map(item => (
          renderDetailItem(item, detailBarMax, props.differences)
        ))}
      </div>
    )
  }

}
